import React, { useCallback, useEffect, useState } from "react";
import {
  Bell,
  Building2,
  CheckCircle2,
  Loader2,
  Mail,
  Percent,
  Plus,
  Save,
  Tags,
  Trash2,
  Wallet,
} from "lucide-react";
import { toast } from "sonner";
import AdminShell from "../../components/layout/AdminShell";
import { useAuth } from "../../context/AuthContext";
import { SettingsApi, errorMessage } from "../../lib/api";
import { formatDateTime, formatMoney } from "../../lib/format";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { useI18n } from "../../i18n";
import { NotificationsApi } from "../../lib/api";

const MODE_KEYS = [
  { key: "vat_percent", labelKey: "settings.modeVat" },
  { key: "gross_percent", labelKey: "settings.modeGross" },
  { key: "both", labelKey: "settings.modeBoth" },
];


const SettingsPage = () => {
  const { t } = useI18n();
  const { currency, refresh, tenant, setTenant, isAccountingFirm } = useAuth();
  const [settings, setSettings] = useState(null);
  const [company, setCompany] = useState({});
  const [rates, setRates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [rules, setRules] = useState(null);
  const [busy, setBusy] = useState(null);
  const [notify, setNotify] = useState(null);
  const [emailInfo, setEmailInfo] = useState(null);
  const [emailLog, setEmailLog] = useState([]);
  const [emailWebhook, setEmailWebhook] = useState(null);
  const [savingOrgType, setSavingOrgType] = useState(false);

  const saveOrgType = async (value) => {
    setSavingOrgType(true);
    try {
      const res = await SettingsApi.setAdminSubType(value);
      setTenant?.(res.tenant);
      toast.success(t("settings.orgTypeSaved"));
    } catch (e) {
      toast.error(errorMessage(e, "Could not change the organisation type"));
    } finally {
      setSavingOrgType(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const data = await SettingsApi.get();
      setSettings(data);
      setCompany(data.company || {});
      setRates(data.vat_rates || []);
      setCategories(data.categories || []);
      setRules({
        ...data.refund_rules,
        category_overrides: data.refund_rules?.category_overrides || {},
      });
      const n = data.notifications || {};
      setNotify({
        invites: n.invites !== false,
        needs_review: n.needs_review !== false,
        quota: n.quota !== false,
        monthly_report: {
          enabled: !!n.monthly_report?.enabled,
          day: n.monthly_report?.day || 1,
          recipients: (n.monthly_report?.recipients || []).join(", "),
          formats: n.monthly_report?.formats || ["pdf", "csv"],
        },
      });
      NotificationsApi.log()
        .then((d) => {
          setEmailLog(d.emails || []);
          setEmailWebhook(d.webhook || null);
          setEmailInfo(d.email || null);
        })
        .catch(() => setEmailLog([]));
    } catch (e) {
      toast.error(errorMessage(e, "Could not load settings"));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveCompany = async () => {
    setBusy("company");
    try {
      await SettingsApi.updateCompany({
        name: company.name,
        country: company.country,
        currency: company.currency,
        tax_id: company.tax_id ?? "",
        address: company.address ?? "",
        auto_approve: !!company.auto_approve,
        allow_overage: company.allow_overage !== false,
        language: company.language || "hr",
      });
toast.success(t("settings.savedCompany"));
      refresh();
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Could not save the company profile"));
    } finally {
      setBusy(null);
    }
  };

  const saveRates = async () => {
    setBusy("rates");
    try {
      await SettingsApi.updateVatRates(
        rates.map((r) => ({
          id: r.id,
          name: r.name,
          percent: Number(r.percent) || 0,
          active: !!r.active,
        }))
      );
toast.success(t("settings.savedVat"));
      refresh();
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Could not save VAT rates"));
    } finally {
      setBusy(null);
    }
  };

  const saveCategories = async () => {
    setBusy("categories");
    try {
      await SettingsApi.updateCategories(categories);
toast.success(t("settings.savedCategories"));
      refresh();
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Could not save categories"));
    } finally {
      setBusy(null);
    }
  };

  const saveRules = async () => {
    setBusy("rules");
    try {
      const payload = {
        mode: rules.mode,
        vat_percent: Number(rules.vat_percent) || 0,
        gross_percent: Number(rules.gross_percent) || 0,
        max_per_receipt:
          rules.max_per_receipt === "" || rules.max_per_receipt == null
            ? null
            : Number(rules.max_per_receipt),
        require_approval: !!rules.require_approval,
        category_overrides: Object.fromEntries(
          Object.entries(rules.category_overrides || {}).map(([k, v]) => [
            k,
            {
              mode: v.mode || "vat_percent",
              vat_percent: Number(v.vat_percent) || 0,
              gross_percent: Number(v.gross_percent) || 0,
              max_per_receipt:
                v.max_per_receipt === "" || v.max_per_receipt == null ? null : Number(v.max_per_receipt),
            },
          ])
        ),
      };
      const res = await SettingsApi.updateRefundRules(payload);
      toast.success(t("settings.savedRules"), {
        description: t("settings.recomputed", { count: res.receipts_recomputed }),
      });
      refresh();
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Could not save refund rules"));
    } finally {
      setBusy(null);
    }
  };

  const saveNotifications = async () => {
    setBusy("notify");
    try {
      await NotificationsApi.updateSettings({
        invites: !!notify.invites,
        needs_review: !!notify.needs_review,
        quota: !!notify.quota,
        monthly_report: {
          enabled: !!notify.monthly_report.enabled,
          day: Number(notify.monthly_report.day) || 1,
          recipients: String(notify.monthly_report.recipients || "")
            .split(",")
            .map((x) => x.trim())
            .filter((x) => x.includes("@")),
          formats: notify.monthly_report.formats,
        },
      });
      toast.success(t("settings.savedNotifications"));
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Could not save notification settings"));
    } finally {
      setBusy(null);
    }
  };

  const sendTest = async () => {
    setBusy("test");
    try {
      await NotificationsApi.test();
      toast.success(t("settings.testSent"));
      NotificationsApi.log().then((d) => {
        setEmailLog(d.emails || []);
        setEmailWebhook(d.webhook || null);
      });
    } catch (e) {
      toast.error(errorMessage(e, "Could not send the test email"));
    } finally {
      setBusy(null);
    }
  };

  const addOverride = (key) =>
    setRules((p) => ({
      ...p,
      category_overrides: {
        ...p.category_overrides,
        [key]: { mode: "vat_percent", vat_percent: 100, gross_percent: 0, max_per_receipt: null },
      },
    }));

  const removeOverride = (key) =>
    setRules((p) => {
      const next = { ...p.category_overrides };
      delete next[key];
      return { ...p, category_overrides: next };
    });

  const setOverride = (key, field, value) =>
    setRules((p) => ({
      ...p,
      category_overrides: {
        ...p.category_overrides,
        [key]: { ...p.category_overrides[key], [field]: value },
      },
    }));

  const previewRefund = (mode, vatPct, grossPct, cap) => {
    const vat = 25;
    const gross = 125;
    let amount = 0;
    if (mode === "vat_percent" || mode === "both") amount += (vat * (Number(vatPct) || 0)) / 100;
    if (mode === "gross_percent" || mode === "both") amount += (gross * (Number(grossPct) || 0)) / 100;
    if (cap != null && cap !== "" && amount > Number(cap)) amount = Number(cap);
    return amount;
  };

  if (!settings || !rules) {
    return (
      <AdminShell title={t("settings.title")}>
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      </AdminShell>
    );
  }

  const availableForOverride = categories.filter((c) => !(c.key in (rules.category_overrides || {})));

  return (
    <AdminShell title={t("settings.title")} subtitle={t("settings.sub")}>
      <Tabs defaultValue={isAccountingFirm ? "vat" : "refunds"} className="w-full">
        <TabsList className="ll-scroll max-w-full justify-start overflow-x-auto" data-testid="settings-tabs">
{!isAccountingFirm ? (
                    <TabsTrigger value="refunds" data-testid="tab-refunds">
            <Wallet className="mr-2 h-4 w-4" /> {t("settings.tabRefunds")}
          </TabsTrigger>
          ) : null}
          <TabsTrigger value="vat" data-testid="tab-vat">
            <Percent className="mr-2 h-4 w-4" /> {t("settings.tabVat")}
          </TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">
            <Tags className="mr-2 h-4 w-4" /> {t("settings.tabCategories")}
          </TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-notifications">
            <Bell className="mr-2 h-4 w-4" /> {t("settings.tabNotifications")}
          </TabsTrigger>
          <TabsTrigger value="company" data-testid="tab-company">
            <Building2 className="mr-2 h-4 w-4" /> {t("settings.tabCompany")}
          </TabsTrigger>
        </TabsList>

        {/* ------------------------------------------------------------ refunds */}
        {!isAccountingFirm ? (
        <TabsContent value="refunds" className="mt-4 space-y-5">
          <div className="ll-card p-4 md:p-5">
            <h2 className="font-display text-base font-semibold">{t("settings.companyDefault")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("settings.companyDefaultSub")}
            </p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-1.5">
                <Label>{t("settings.refundBasis")}</Label>
                <Select value={rules.mode} onValueChange={(v) => setRules((p) => ({ ...p, mode: v }))}>
                  <SelectTrigger data-testid="refund-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MODE_KEYS.map((m) => (
                      <SelectItem key={m.key} value={m.key}>
                        {t(m.labelKey)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="vat_percent">{t("settings.ofVatPct")}</Label>
                <Input
                  id="vat_percent"
                  inputMode="decimal"
                  className="ll-num"
                  value={rules.vat_percent ?? 0}
                  disabled={rules.mode === "gross_percent"}
                  onChange={(e) => setRules((p) => ({ ...p, vat_percent: e.target.value }))}
                  data-testid="refund-vat-percent-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="gross_percent">{t("settings.ofTotalPct")}</Label>
                <Input
                  id="gross_percent"
                  inputMode="decimal"
                  className="ll-num"
                  value={rules.gross_percent ?? 0}
                  disabled={rules.mode === "vat_percent"}
                  onChange={(e) => setRules((p) => ({ ...p, gross_percent: e.target.value }))}
                  data-testid="refund-gross-percent-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cap">
                  {t("settings.capPerReceipt")} ({currency})
                </Label>
                <Input
                  id="cap"
                  inputMode="decimal"
                  className="ll-num"
                  placeholder={t("settings.noCap")}
                  value={rules.max_per_receipt ?? ""}
                  onChange={(e) => setRules((p) => ({ ...p, max_per_receipt: e.target.value }))}
                  data-testid="refund-cap-input"
                />
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/30 p-3">
              <p className="text-sm text-muted-foreground">
                {t("settings.example", {
                  gross: formatMoney(125, currency),
                  vat: formatMoney(25, currency),
                })}{" "}
                <span className="ll-num font-semibold text-primary">
                  {formatMoney(
                    previewRefund(rules.mode, rules.vat_percent, rules.gross_percent, rules.max_per_receipt),
                    currency
                  )}
                </span>
              </p>
              <Button onClick={saveRules} disabled={busy === "rules"} data-testid="save-refund-rules">
                {busy === "rules" ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                {t("settings.saveRecalculate")}
              </Button>
            </div>
          </div>

          <div className="ll-card overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
              <div>
                <h2 className="font-display text-base font-semibold">{t("settings.perCategory")}</h2>
                <p className="text-xs text-muted-foreground">
                  {t("settings.perCategorySub")}
                </p>
              </div>
              {availableForOverride.length ? (
                <Select onValueChange={addOverride}>
                  <SelectTrigger className="h-9 w-[220px]" data-testid="add-override-select">
                    <SelectValue placeholder={t("settings.addCategoryRule")} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableForOverride.map((c) => (
                      <SelectItem key={c.key} value={c.key}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : null}
            </div>
            {Object.keys(rules.category_overrides || {}).length === 0 ? (
              <p className="p-4 text-sm text-muted-foreground">
                {t("settings.noOverrides")}
              </p>
            ) : (
              <div className="ll-scroll overflow-x-auto">
                <Table data-testid="category-overrides-table">
                  <TableHeader className="bg-muted/40">
                    <TableRow>
                      <TableHead>{t("common.category")}</TableHead>
                      <TableHead>{t("settings.colBasis")}</TableHead>
                      <TableHead className="w-[110px]">{t("settings.ofVatPct")}</TableHead>
                      <TableHead className="w-[110px]">{t("settings.ofTotalPct")}</TableHead>
                      <TableHead className="w-[120px]">{t("override.cap")}</TableHead>
                      <TableHead className="text-right">{t("settings.colExample")}</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(rules.category_overrides).map(([key, ov]) => (
                      <TableRow key={key} data-testid="category-override-row">
                        <TableCell className="font-medium">
                          {categories.find((c) => c.key === key)?.label || key}
                        </TableCell>
                        <TableCell>
                          <Select value={ov.mode} onValueChange={(v) => setOverride(key, "mode", v)}>
                            <SelectTrigger className="h-8 w-[170px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {MODE_KEYS.map((m) => (
                                <SelectItem key={m.key} value={m.key}>
                                  {t(m.labelKey)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input
                            className="ll-num h-8"
                            inputMode="decimal"
                            value={ov.vat_percent ?? 0}
                            onChange={(e) => setOverride(key, "vat_percent", e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            className="ll-num h-8"
                            inputMode="decimal"
                            value={ov.gross_percent ?? 0}
                            onChange={(e) => setOverride(key, "gross_percent", e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            className="ll-num h-8"
                            inputMode="decimal"
                            placeholder={t("settings.noCap")}
                            value={ov.max_per_receipt ?? ""}
                            onChange={(e) => setOverride(key, "max_per_receipt", e.target.value)}
                          />
                        </TableCell>
                        <TableCell className="ll-num text-right text-primary">
                          {formatMoney(
                            previewRefund(ov.mode, ov.vat_percent, ov.gross_percent, ov.max_per_receipt),
                            currency
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => removeOverride(key)}
                            data-testid="remove-override-button"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </TabsContent>
        ) : null}

        {/* ---------------------------------------------------------------- vat */}
        <TabsContent value="vat" className="mt-4">
          <div className="ll-card overflow-hidden">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <div>
                <h2 className="font-display text-base font-semibold">{t("settings.vatRatesTitle")}</h2>
                <p className="text-xs text-muted-foreground">
                  {t("settings.vatRatesSub")}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setRates((p) => [
                    ...p,
                    { id: `rate${p.length}${Date.now() % 1000}`, name: "New rate", percent: 0, active: true },
                  ])
                }
                data-testid="add-vat-rate-button"
              >
                <Plus className="mr-2 h-4 w-4" /> {t("settings.addRate")}
              </Button>
            </div>
            <Table data-testid="vat-rates-table">
              <TableHeader className="bg-muted/40">
                <TableRow>
                  <TableHead>{t("common.name")}</TableHead>
                  <TableHead className="w-[140px]">{t("settings.colPercent")}</TableHead>
                  <TableHead className="w-[120px]">{t("settings.colActive")}</TableHead>
                  <TableHead className="w-[80px] text-right">{t("team.colRemove")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rates.map((r, i) => (
                  <TableRow key={r.id || i} data-testid="vat-rate-row">
                    <TableCell>
                      <Input
                        className="h-8"
                        value={r.name}
                        onChange={(e) =>
                          setRates((p) => p.map((x, xi) => (xi === i ? { ...x, name: e.target.value } : x)))
                        }
                        data-testid="vat-rate-name-input"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        className="ll-num h-8"
                        inputMode="decimal"
                        value={r.percent}
                        onChange={(e) =>
                          setRates((p) => p.map((x, xi) => (xi === i ? { ...x, percent: e.target.value } : x)))
                        }
                        data-testid="vat-rate-percent-input"
                      />
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={!!r.active}
                        onCheckedChange={(v) =>
                          setRates((p) => p.map((x, xi) => (xi === i ? { ...x, active: v } : x)))
                        }
                        data-testid="vat-rate-active-switch"
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setRates((p) => p.filter((_, xi) => xi !== i))}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex justify-end border-t p-3">
              <Button onClick={saveRates} disabled={busy === "rates"} data-testid="save-vat-rates">
                {busy === "rates" ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                {t("settings.saveVat")}
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* --------------------------------------------------------- categories */}
        <TabsContent value="categories" className="mt-4">
          <div className="ll-card overflow-hidden">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <h2 className="font-display text-base font-semibold">{t("settings.categoriesTitle")}</h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setCategories((p) => [...p, { key: `custom_${p.length}`, label: "New category", color: "#64748B" }])
                }
                data-testid="add-category-button"
              >
                <Plus className="mr-2 h-4 w-4" /> {t("settings.addCategory")}
              </Button>
            </div>
            <Table data-testid="categories-table">
              <TableHeader className="bg-muted/40">
                <TableRow>
                  <TableHead>{t("settings.colLabel")}</TableHead>
                  <TableHead className="w-[200px]">{t("settings.colKey")}</TableHead>
                  <TableHead className="w-[120px]">{t("settings.colColour")}</TableHead>
                  <TableHead className="w-[80px] text-right">{t("team.colRemove")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {categories.map((c, i) => (
                  <TableRow key={`${c.key}-${i}`} data-testid="category-row">
                    <TableCell>
                      <Input
                        className="h-8"
                        value={c.label}
                        onChange={(e) =>
                          setCategories((p) => p.map((x, xi) => (xi === i ? { ...x, label: e.target.value } : x)))
                        }
                      />
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{c.key}</TableCell>
                    <TableCell>
                      <input
                        type="color"
                        aria-label={`Colour for ${c.label}`}
                        className="h-8 w-14 cursor-pointer rounded border bg-card"
                        value={c.color}
                        onChange={(e) =>
                          setCategories((p) => p.map((x, xi) => (xi === i ? { ...x, color: e.target.value } : x)))
                        }
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setCategories((p) => p.filter((_, xi) => xi !== i))}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex justify-end border-t p-3">
              <Button onClick={saveCategories} disabled={busy === "categories"} data-testid="save-categories">
                {busy === "categories" ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                {t("settings.saveCategories")}
              </Button>
            </div>
          </div>
        </TabsContent>


        {/* --------------------------------------------------------- notifications */}
        <TabsContent value="notifications" className="mt-4 space-y-5">
          {notify ? (
            <>

              <div className="ll-card p-4 md:p-5">
                <h2 className="font-display text-base font-semibold">{t("settings.notifTitle")}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{t("settings.notifSub")}</p>

                <div className="mt-4 space-y-3">
                  {[
                    ["invites", "settings.notifInvites", "settings.notifInvitesSub"],
                    ["needs_review", "settings.notifReview", "settings.notifReviewSub"],
                    ["quota", "settings.notifQuota", "settings.notifQuotaSub"],
                  ].map(([key, titleKey, subKey]) => (
                    <div key={key} className="flex items-center justify-between gap-4 rounded-lg border p-3">
                      <div>
                        <p className="text-sm font-medium">{t(titleKey)}</p>
                        <p className="text-xs text-muted-foreground">{t(subKey)}</p>
                      </div>
                      <Switch
                        checked={!!notify[key]}
                        onCheckedChange={(v) => setNotify((p) => ({ ...p, [key]: v }))}
                        data-testid={`notify-${key}-switch`}
                      />
                    </div>
                  ))}
                </div>

                {!isAccountingFirm ? (
                <div className="mt-4 rounded-lg border p-3">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium">{t("settings.monthlyReport")}</p>
                      <p className="text-xs text-muted-foreground">{t("settings.monthlyReportSub")}</p>
                    </div>
                    <Switch
                      checked={!!notify.monthly_report.enabled}
                      onCheckedChange={(v) =>
                        setNotify((p) => ({
                          ...p,
                          monthly_report: { ...p.monthly_report, enabled: v },
                        }))
                      }
                      data-testid="monthly-report-switch"
                    />
                  </div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">{t("settings.monthlyDay")}</Label>
                      <Input
                        type="number"
                        min={1}
                        max={28}
                        className="ll-num"
                        value={notify.monthly_report.day}
                        onChange={(e) =>
                          setNotify((p) => ({
                            ...p,
                            monthly_report: { ...p.monthly_report, day: e.target.value },
                          }))
                        }
                        data-testid="monthly-day-input"
                      />
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label className="text-xs">{t("settings.monthlyRecipients")}</Label>
                      <Input
                        placeholder="knjigovodstvo@example.hr, ana@example.hr"
                        value={notify.monthly_report.recipients}
                        onChange={(e) =>
                          setNotify((p) => ({
                            ...p,
                            monthly_report: { ...p.monthly_report, recipients: e.target.value },
                          }))
                        }
                        data-testid="monthly-recipients-input"
                      />
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-4">
                    <span className="text-xs text-muted-foreground">{t("settings.monthlyFormats")}</span>
                    {["pdf", "csv"].map((f) => (
                      <label key={f} className="flex cursor-pointer items-center gap-2 text-sm">
                        <Switch
                          checked={notify.monthly_report.formats.includes(f)}
                          onCheckedChange={(v) =>
                            setNotify((p) => ({
                              ...p,
                              monthly_report: {
                                ...p.monthly_report,
                                formats: v
                                  ? [...new Set([...p.monthly_report.formats, f])]
                                  : p.monthly_report.formats.filter((x) => x !== f),
                              },
                            }))
                          }
                          data-testid={`monthly-format-${f}`}
                        />
                        {f.toUpperCase()}
                      </label>
                    ))}
                  </div>
                </div>
                ) : null}

                <div className="mt-4 flex flex-wrap justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={sendTest}
                    disabled={busy === "test"}
                    data-testid="send-test-email"
                  >
                    {busy === "test" ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Mail className="mr-2 h-4 w-4" />
                    )}
                    {t("settings.sendTest")}
                  </Button>
                  <Button
                    onClick={saveNotifications}
                    disabled={busy === "notify"}
                    data-testid="save-notifications"
                  >
                    {busy === "notify" ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="mr-2 h-4 w-4" />
                    )}
                    {t("settings.saveNotifications")}
                  </Button>
                </div>
              </div>

              <div className="ll-card overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
                  <div>
                    <h2 className="font-display text-sm font-semibold">{t("settings.emailLog")}</h2>

                  </div>
                </div>
                {emailLog.length ? (
                  <div className="ll-scroll overflow-x-auto">
                    <Table data-testid="email-log-table">
                      <TableHeader className="bg-muted/40">
                        <TableRow>
                          <TableHead>{t("common.date")}</TableHead>
                          <TableHead>{t("settings.logTo")}</TableHead>
                          <TableHead>{t("settings.logSubject")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {emailLog.map((e) => (
                          <TableRow key={e.id} data-testid="email-log-row">
                            <TableCell className="whitespace-nowrap text-xs">
                              {formatDateTime(e.created_at)}
                            </TableCell>
                            <TableCell className="max-w-[180px] truncate text-sm">
                              {(e.to || []).join(", ")}
                            </TableCell>
                            <TableCell className="max-w-[260px] truncate text-sm">{e.subject}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="p-4 text-sm text-muted-foreground">{t("settings.noEmails")}</p>
                )}
              </div>
            </>
          ) : null}
        </TabsContent>

        {/* ------------------------------------------------------------ company */}
        <TabsContent value="company" className="mt-4 space-y-5">
          <div className="ll-card p-4 md:p-5" data-testid="org-type-card">
            <h2 className="font-display text-base font-semibold">{t("auth.orgTypeLabel")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t("settings.orgTypeSub")}</p>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {["fleet_owner", "accounting_firm"].map((key) => {
                const active = (tenant?.admin_sub_type || "fleet_owner") === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => saveOrgType(key)}
                    disabled={active || savingOrgType}
                    aria-pressed={active}
                    className={`rounded-xl border p-3 text-left transition-colors duration-150 ${
                      active
                        ? "border-primary bg-primary/5 ring-1 ring-primary"
                        : "hover:bg-accent focus-visible:bg-accent"
                    }`}
                    data-testid={`org-type-${key}`}
                  >
                    <span className="text-sm font-medium">{t(`auth.orgType_${key}`)}</span>
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {t(`auth.orgType_${key}_hint`)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="ll-card max-w-2xl p-4 md:p-5">
            <h2 className="font-display text-base font-semibold">{t("settings.companyProfile")}</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="cname">{t("auth.companyName")}</Label>
                <Input
                  id="cname"
                  value={company.name || ""}
                  onChange={(e) => setCompany((p) => ({ ...p, name: e.target.value }))}
                  data-testid="company-name-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ctax">{t("drawer.taxIdField")}</Label>
                <Input
                  id="ctax"
                  value={company.tax_id || ""}
                  onChange={(e) => setCompany((p) => ({ ...p, tax_id: e.target.value }))}
                  data-testid="company-taxid-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ccurrency">{t("common.currency")}</Label>
                <Select
                  value={company.currency || "EUR"}
                  onValueChange={(v) => setCompany((p) => ({ ...p, currency: v }))}
                >
                  <SelectTrigger id="ccurrency" data-testid="company-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {["EUR", "USD", "GBP", "PLN", "RON"].map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="caddress">{t("settings.addressField")}</Label>
                <Input
                  id="caddress"
                  value={company.address || ""}
                  onChange={(e) => setCompany((p) => ({ ...p, address: e.target.value }))}
                  data-testid="company-address-input"
                />
              </div>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="clang">{t("common.language")}</Label>
                <Select
                  value={company.language || "hr"}
                  onValueChange={(v) => setCompany((p) => ({ ...p, language: v }))}
                >
                  <SelectTrigger id="clang" data-testid="company-language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hr">Hrvatski</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between gap-4 rounded-lg border p-3">
              <div>
                <p className="text-sm font-medium">{t("settings.allowOverage")}</p>
                <p className="text-xs text-muted-foreground">{t("settings.allowOverageSub")}</p>
              </div>
              <Switch
                checked={company.allow_overage !== false}
                onCheckedChange={(v) => setCompany((p) => ({ ...p, allow_overage: v }))}
                data-testid="allow-overage-switch"
              />
            </div>

            <div className="mt-4 flex items-center justify-between gap-4 rounded-lg border p-3">
              <div>
                <p className="text-sm font-medium">{t("settings.autoApprove")}</p>
                <p className="text-xs text-muted-foreground">
                  {t("settings.autoApproveSub")}
                </p>
              </div>
              <Switch
                checked={!!company.auto_approve}
                onCheckedChange={(v) => setCompany((p) => ({ ...p, auto_approve: v }))}
                data-testid="auto-approve-switch"
              />
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={saveCompany} disabled={busy === "company"} data-testid="save-company">
                {busy === "company" ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                {t("settings.saveProfile")}
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </AdminShell>
  );
};

export default SettingsPage;
